import { SUPPORTED_LANGUAGES } from '@/constants/constants';
import { getUserAddDataFromLocalStorage } from './userUtils.js';

/**
 * Gets the user's locale from browser settings with fallback options
 * @param {string} defaultLocale - Fallback locale if browser locale can't be determined
 * @returns {string} - The detected locale or default
 */
export function getUserLocale(defaultLocale = 'de-DE') {
    // Try to get locale from navigator
    if (typeof window !== 'undefined' && window.navigator) {
      // Option 1: navigator.language (most browsers)
      if (navigator.language) {
        return navigator.language;
      }
      
      // Option 2: navigator.languages (array of preferred languages in Chrome, Firefox)
      if (navigator.languages && navigator.languages.length) {
        return navigator.languages[0];
      }
      
      // Option 3: older IE browsers
      if (navigator.userLanguage) {
        return navigator.userLanguage;
      }
      
      // Option 4: older Safari browsers
      if (navigator.browserLanguage) {
        return navigator.browserLanguage;
      }
    }
    
    // Fallback to default locale
    return defaultLocale;
  }
  
  /**
   * Maps a locale to a standardized format
   * @param {string} locale - The locale string (e.g., 'en', 'en-US', 'de')
   * @returns {string} - Standardized locale (e.g., 'en-US', 'de-DE')
   */
  export function normalizeLocale(locale) {
    if (!locale) {
      return 'de-DE'; // Default fallback
    }
    
    // If locale already has country code, return as is
    if (locale.includes('-')) {
      return locale;
    }
    
    // Map language codes to their default country variants
    const languageToLocale = {
      'en': 'en-US',
      'de': 'de-DE',
      'fr': 'fr-FR',
      'es': 'es-ES',
      'it': 'it-IT',
      'pt': 'pt-PT',
      'nl': 'nl-NL',
      'pl': 'pl-PL',
      'cs': 'cs-CZ',
      'da': 'da-DK',
      // Add more mappings as needed
    };
    
    return languageToLocale[locale] || `${locale}-${locale.toUpperCase()}`;
  }

  /**
   * Gets the user's locale from stored user data with fallback to browser locale
   * Uses the same language-to-locale mapping as dateUtilsV2.js for consistency
   * @param {string} defaultLocale - Fallback locale if user data can't be determined
   * @returns {string} - The user's locale or default
   */
  export function getUserLocaleFromStoredData(defaultLocale = 'de-DE') {
    try {
      const { language } = getUserAddDataFromLocalStorage();
      
      if (language) {
        // Map language to locale following the same pattern as dateUtilsV2.js
        const localeMap = {
          'cs': 'cs-CZ',    // Czech
          'da': 'da-DK',    // Danish
          'nl': 'nl-NL',    // Dutch
          'en': 'en-GB',    // English (GB) - consistent with dateUtilsV2.js
          'fr': 'fr-FR',    // French
          'de': 'de-DE',    // German
          'it': 'it-IT',    // Italian
          'pl': 'pl-PL',    // Polish
          'pt': 'pt-PT',    // Portuguese
          'es': 'es-ES'     // Spanish
        };
        
        const locale = localeMap[language] || defaultLocale;
        return normalizeLocale(getUserLocale(locale));
      }
    } catch (error) {
      console.error('Error getting user locale from stored data:', error);
    }
    
    // Fallback to browser locale if user data is not available
    return normalizeLocale(getUserLocale(defaultLocale));
  }


  export const isLanguageSupported = (lang) =>
    SUPPORTED_LANGUAGES.some((supported) => supported.value === lang);